import React, { useEffect, useMemo, useState } from 'react';
import { IconButton, Typography, useTheme } from '@material-ui/core';
import Add from '@material-ui/icons/Add';
import Remove from '@material-ui/icons/Remove';
import Delete from '@material-ui/icons/Delete';
import { Tooltip, usePhoneView } from '@michelin/acid-components';
import { useTranslation } from '@michelin/central-provider';
import debounce from 'lodash/debounce';
import noop from 'lodash/noop';

export interface QtySelectorProps {
  value: number;
  onChange?: (value: number) => void;
  defaultValue: number;
  maxValue: number;
  onRemove?: () => void;
  onAdd?: () => void;
  selected: boolean;
  disabled?: boolean;
  readonly?: boolean;
  tiresCount?: number;
  small?: boolean;
  shoppingCartMode?: boolean;
}

export default function QtySelector(props: QtySelectorProps) {
  const {
    value,
    onChange = () => {},
    onRemove = () => {},
    selected,
    onAdd = () => {},
    defaultValue,
    maxValue,
    tiresCount,
    small,
    disabled: disabledProp,
    readonly,
    shoppingCartMode = false,
  } = props;

  const disabled = disabledProp || readonly;

  const theme = useTheme();
  const { t } = useTranslation();
  const [qty, setQty] = useState(value);
  const mobileView = usePhoneView();

  const changeQty = useMemo(
    () =>
      debounce((newValue: number) => {
        onChange(newValue);
      }, 800),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    if (qty === 0) {
      onRemove();
      setQty(defaultValue);
    }
    if (qty > 0 && qty !== value) changeQty(qty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qty]);

  useEffect(() => {
    if (value !== qty) setQty(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const width = 100;

  const disableByQty = (tiresCount && tiresCount >= maxValue && !selected) || (!selected && disabledProp);

  let backgroundColor: string | undefined =
    (tiresCount && tiresCount >= maxValue && !selected) || disabled
      ? theme.palette.secondary.main
      : theme.palette.primary.main;
  if (selected && disabledProp) backgroundColor = undefined;

  const maxQtyReached: boolean = (!tiresCount && qty >= maxValue) || (!!tiresCount && tiresCount >= maxValue);

  const addToCartButtonActionIsEnabled = !disabled && ((tiresCount && tiresCount < maxValue) || !tiresCount);
  const addToCartButtonAction = addToCartButtonActionIsEnabled
    ? () => {
        setQty(defaultValue);
        onAdd();
      }
    : noop;

  return (
    <div style={{ display: 'flex', alignItems: 'center'}}>
      <Tooltip
        title={
          tiresCount && tiresCount >= maxValue && !selected
            ? `On-vehicle orders cannot exceed ${maxValue} tires per order.` +
              'Please review your current tire selection and make the appropriate changes.'
            : ''
        }
      >
        <div
          style={{
            height: 40,
            background: backgroundColor,
            width,
            borderRadius: 20,
            overflow: disabledProp ? undefined : 'hidden',
            transform: mobileView || small ? 'scale(0.8)' : undefined,
          }}
        >
          <>
              {selected && !disabledProp && (
              <>
                <IconButton
                  onClick={() => {
                    if (qty > 0) setQty(qty - 1);
                  }}
                  style={{
                    float: 'left',
                    width: 40,
                    height: 40,
                    margin: 0,
                    color: disabled ? undefined : 'white',
                  }}
                  id="decreaseQty"
                  disabled={disabled}
                >
                  <Remove />
                </IconButton>
                {!maxQtyReached && (
                  <IconButton
                    onClick={() => {
                      if ((tiresCount && tiresCount - value + qty < maxValue) || (!tiresCount && qty < maxValue))
                        setQty(qty + 1);
                    }}
                    style={{
                      float: 'right',
                      width: 40,
                      height: 40,
                      margin: 0,
                      color: disabled ? undefined : 'white',
                    }}
                    id="increaseQty"
                    disabled={disabled}
                  >
                    <Add />
                  </IconButton>
                )}
                <Typography
                  style={{
                    whiteSpace: 'nowrap',
                    fontSize: 14,
                    margin: 0,
                    width: maxQtyReached ? 40 : undefined,
                    height: 40,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    color: disabled ? 'rgba(0, 0, 0, 0.26)' : 'white',
                  }}
                >
                  {qty}
                  {maxQtyReached && ` ${t('(max)')}`}
                </Typography>
              </>
            )}
            {selected && disabledProp && (
              <Typography
                style={{
                  whiteSpace: 'nowrap',
                  fontSize: 14,
                  margin: 0,
                  fontWeight: 'bold',
                  transform: mobileView ? 'scale(1.4)' : 'scale(1)',
                }}
              >
                {`Qty: ${qty}`}
              </Typography>
            )}
            {!selected && (
              <div
                onClick={() => addToCartButtonAction()}
                onKeyDown={() => addToCartButtonAction()}
                // TODO: check and properly set tabIndex
                tabIndex={0}
                style={{
                  cursor: !disabled && ((tiresCount && tiresCount < maxValue) || !tiresCount) ? 'pointer' : undefined,
                }}
                role="button"
                id="addToCartButton"
              >
                <IconButton
                  disabled={disabled}
                  style={{
                    float: 'left',
                    width: 12,
                    height: 12,
                    marginTop: 14,
                    marginLeft: 8,
                    marginRight: 8,
                    padding: 0,
                    color: disableByQty ? undefined : 'white',
                  }}
                >
                  <Add />
                </IconButton>
                <Typography
                  style={{
                    whiteSpace: 'nowrap',
                    fontSize: 14,
                    margin: 0,
                    width: 55,
                    height: 40,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    color: disableByQty ? undefined : 'white',
                  }}
                >
                  {defaultValue === 1 && t('Add')}
                  {defaultValue > 1 && `Add ${defaultValue}`}
                  {!!tiresCount && tiresCount >= maxValue && t('max')}
                </Typography>
              </div>
            )}
          </>
        </div>
      </Tooltip>
      {shoppingCartMode && selected && qty > 0 && 
        <IconButton
          onClick={() => setQty(0)}
          style={{
            width: 30,
            height: 30,
          }}
          color="primary"
          id="deletePart"
          size='small'
          disabled={disabled}
        >
          <Delete />
        </IconButton>
      }
    </div>
  );
}
